import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Shraaddham & Tharpanam': { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-800', icon: '🔥' },
  'Rituals & Puja': { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', icon: '🙏' },
  'Marriage & Grihapravesam': { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-800', icon: '💍' },
  'Panchangam & Dates': { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', icon: '📅' },
  'General Dharma': { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', icon: '📚' },
};

const QAPage = () => {
  const { language, user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedQ, setExpandedQ] = useState(null);
  
  // Form State
  const [showForm, setShowForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [subject, setSubject] = useState('');
  const [authorName, setAuthorName] = useState(user ? user.name : '');
  const [authorEmail, setAuthorEmail] = useState(user ? user.email : '');
  const [authorPhone, setAuthorPhone] = useState('');
  const [authorLocation, setAuthorLocation] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(true);
  const [notifyComments, setNotifyComments] = useState(true);
  const [isHuman, setIsHuman] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [currentPage, selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/qa/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      let url = `${API}/qa/list?page=${currentPage}&limit=15`;
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url);
      const data = response.data;
      setQuestions(data.items || []);
      setTotalPages(data.pages || 1);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch Q&A');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchQuestions();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isHuman) {
      alert(language === 'ta' ? 'நீங்கள் மனிதர் என்பதை உறுதிப்படுத்தவும்' : 'Please check the spam protection box.');
      return;
    }
    
    try {
      setSubmitStatus('loading');
      setErrorMessage('');
      await axios.post(`${API}/qa/ask`, {
        subject, question: newQuestion, author_name: authorName,
        author_email: authorEmail, author_phone: authorPhone,
        author_location: authorLocation, whatsapp_number: whatsappNumber,
        notify_whatsapp: notifyWhatsapp, notify_comments: notifyComments,
        captcha_answer: 'verified'
      });
      setSubmitStatus('success');
      setSubject(''); setNewQuestion(''); setWhatsappNumber('');
      setAuthorPhone(''); setAuthorLocation(''); setIsHuman(false);
      setTimeout(() => { setShowForm(false); setSubmitStatus(null); }, 3000);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error.response?.data?.detail || 'An error occurred.');
    }
  };

  const catStyle = (cat) => CATEGORY_COLORS[cat] || { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-800', icon: '📋' };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold text-green-900 mb-3">
            {language === 'ta' ? 'கேள்வி-பதில் பகுதி' : 'Q & A Discussion Board'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'ta' 
              ? 'சாஸ்திரம், தர்மம் மற்றும் பொதுவான கேள்விகளுக்கு ஆச்சார்யர்களின் பதில்கள்.' 
              : 'Expert answers on Shastra, Dharma, Rituals, and Vedic traditions.'}
          </p>
          <p className="text-sm text-gray-500 mt-1">{totalCount} Q&A threads available</p>
          <button 
            onClick={() => setShowForm(!showForm)}
            className="mt-4 px-6 py-3 bg-green-600 text-white font-bold rounded-full shadow-lg hover:bg-green-700 transition"
          >
            {language === 'ta' ? 'கேள்வி கேட்க' : '❓ Ask a Question'}
          </button>
        </div>

        {/* Ask Question Form */}
        {showForm && (
          <div className="bg-white p-8 rounded-xl shadow-md mb-8 border-t-4 border-green-500">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              {language === 'ta' ? 'உங்கள் கேள்வியை சமர்ப்பிக்கவும்' : 'Submit your Question'}
            </h2>
            {submitStatus === 'success' ? (
              <div className="bg-green-100 text-green-800 p-4 rounded-lg font-medium text-center">
                ✅ {language === 'ta' 
                  ? 'உங்கள் கேள்வி வெற்றிகரமாக சமர்ப்பிக்கப்பட்டது!' 
                  : 'Your question was submitted successfully! It will be answered after review.'}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {submitStatus === 'error' && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg border border-red-200 text-sm">{errorMessage}</div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input required value={authorName} onChange={e => setAuthorName(e.target.value)} className="w-full p-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email (Not published) *</label>
                    <input required type="email" value={authorEmail} onChange={e => setAuthorEmail(e.target.value)} className="w-full p-3 border rounded-lg" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
                    <input value={whatsappNumber} onChange={e => setWhatsappNumber(e.target.value)} placeholder="+91..." className="w-full p-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input value={authorPhone} onChange={e => setAuthorPhone(e.target.value)} className="w-full p-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City / Location</label>
                    <input value={authorLocation} onChange={e => setAuthorLocation(e.target.value)} className="w-full p-3 border rounded-lg" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input required value={subject} onChange={e => setSubject(e.target.value)} className="w-full p-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Question *</label>
                  <textarea required rows="4" value={newQuestion} onChange={e => setNewQuestion(e.target.value)} className="w-full p-3 border rounded-lg"></textarea>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <p className="font-medium text-gray-800 mb-2">Notification Preferences</p>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" checked={notifyWhatsapp} onChange={e => setNotifyWhatsapp(e.target.checked)} className="form-checkbox h-5 w-5 text-indigo-600" />
                      <span className="text-gray-700 text-sm">Send answer via WhatsApp</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input type="checkbox" checked={notifyComments} onChange={e => setNotifyComments(e.target.checked)} className="form-checkbox h-5 w-5 text-indigo-600" />
                      <span className="text-gray-700 text-sm">Notify on further comments</span>
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input required type="checkbox" checked={isHuman} onChange={e => setIsHuman(e.target.checked)} className="form-checkbox h-6 w-6 text-green-600" />
                    <span className="text-gray-800 font-medium">I am not a robot (Spam Protection)</span>
                  </label>
                </div>
                <button type="submit" disabled={submitStatus === 'loading'} className="w-full md:w-auto px-8 py-3 bg-indigo-600 text-white font-bold rounded-lg shadow hover:bg-indigo-700 disabled:opacity-50">
                  {submitStatus === 'loading' ? 'Submitting...' : 'Submit Question'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <input 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search questions & answers..."
                className="flex-1 p-2.5 border rounded-lg text-sm"
              />
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
                🔍 Search
              </button>
              {(searchQuery || selectedCategory) && (
                <button type="button" onClick={() => { setSearchQuery(''); setSelectedCategory(null); setCurrentPage(1); }} 
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300">
                  ✕ Clear
                </button>
              )}
            </form>
          </div>
          
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 mt-3">
            <button 
              onClick={() => { setSelectedCategory(null); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                !selectedCategory ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({totalCount})
            </button>
            {categories.map(cat => {
              const style = catStyle(cat.name);
              return (
                <button key={cat.name}
                  onClick={() => { setSelectedCategory(cat.name); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                    selectedCategory === cat.name 
                      ? `${style.bg} ${style.text} ring-2 ring-offset-1 ring-current` 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {style.icon} {cat.name} ({cat.count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Q&A Feed */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin text-4xl">🕉️</div><p className="mt-2 text-gray-500">Loading Q&A...</p></div>
          ) : questions.length === 0 ? (
            <div className="text-center bg-white p-12 rounded-xl shadow">
              <p className="text-gray-500 text-lg">No questions found. {searchQuery ? 'Try a different search term.' : 'Be the first to ask!'}</p>
            </div>
          ) : (
            questions.map(q => {
              const style = catStyle(q.category);
              const isExpanded = expandedQ === q.id;
              return (
                <div key={q.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                  {/* Question Header */}
                  <div 
                    className={`p-5 cursor-pointer ${style.bg} border-l-4 ${style.border}`}
                    onClick={() => setExpandedQ(isExpanded ? null : q.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {q.category && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                              {style.icon} {q.category}
                            </span>
                          )}
                          {q.source === 'gmail_archive' && (
                            <span className="text-xs text-gray-400">📧 Archive</span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {q.subject || 'Question'}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {!isExpanded && q.question && q.question.substring(0, 150) + (q.question.length > 150 ? '...' : '')}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Asked by <span className="font-semibold">{q.author_name}</span>
                          {q.author_location && <span> • 📍 {q.author_location}</span>}
                          {q.created_at && <span> • {new Date(q.created_at).toLocaleDateString()}</span>}
                        </p>
                      </div>
                      <span className="text-gray-400 text-xl ml-2">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t">
                      {/* Full Question */}
                      <div className="p-5 bg-gray-50">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl opacity-60">❓</span>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-500 mb-1">Question</p>
                            <div className="text-gray-800 whitespace-pre-wrap">{q.question}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Answer */}
                      <div className="p-5 bg-white">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">📝</span>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-green-600 mb-1">Answer by Pandit</p>
                            <div className="text-gray-800 whitespace-pre-wrap leading-relaxed">{q.answer}</div>
                            {q.answered_at && (
                              <p className="text-xs text-green-500 mt-3">
                                Answered on {new Date(q.answered_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Followups */}
                      {q.followups && q.followups.length > 0 && (
                        <div className="p-5 bg-blue-50 border-t">
                          <p className="text-sm font-semibold text-blue-700 mb-3">💬 Follow-up Discussion ({q.followups.length})</p>
                          {q.followups.map((f, idx) => (
                            <div key={idx} className={`mb-2 p-3 rounded-lg ${f.is_admin ? 'bg-green-50 border-l-4 border-green-400' : 'bg-white border-l-4 border-gray-300'}`}>
                              <p className="text-sm whitespace-pre-wrap">{f.content}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {f.is_admin ? '🟢 Admin' : f.author_name} • {new Date(f.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8 mb-8">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              ← Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QAPage;
